!(function () {
  "use strict";

  const SELECTORS = {
    formWrapper: "[data-form-wrapper]",
    headerSticky: "[data-header-sticky]",
    mediaList: "[data-product-media-list]",
    slide: "[data-slide]",
  };

  const STICKY_CLASS = "is-sticky";
  const DEFAULT_SPACING = 30;

  if (customElements.get("product-sticky")) return;

  customElements.define(
    "product-sticky",
    class extends HTMLElement {
      constructor() {
        super();
        this.formWrapper = this.querySelector(SELECTORS.formWrapper);
        this.threshold = 0;
        this.stickyTop = DEFAULT_SPACING;
        this.isStuck = false;
        this.scrollHandler = (e) => this.onScroll(e);
        this.resizeHandler = () => this.onResize();
      }

      connectedCallback() {
        if (!this.formWrapper) return;

        this.init();
        document.addEventListener("theme:resize", this.resizeHandler);
      }

      init() {
        if (window.theme.isMobile()) {
          this.teardown();
          return;
        }

        const mediaList = this.querySelector(SELECTORS.mediaList);
        if (!mediaList) return;

        // Only enable sticky when form is shorter than the image column
        if (this.formWrapper.offsetHeight >= mediaList.offsetHeight) {
          this.teardown();
          return;
        }

        this.calculateStickyTop();
        this.calculateThreshold();
        this.engage();
      }

      calculateStickyTop() {
        const stickyHeader = document.querySelector(SELECTORS.headerSticky);

        if (stickyHeader) {
          const { headerHeight } = window.theme.readHeights();
          this.stickyTop = headerHeight;
        } else {
          this.stickyTop = DEFAULT_SPACING;
        }
      }

      calculateThreshold() {
        const slides = this.querySelectorAll(SELECTORS.slide);

        // Fewer than 3 images — stay sticky indefinitely
        if (slides.length < 3) {
          this.threshold = Infinity;
          return;
        }

        const thirdSlide = slides[2];
        const slideDocTop =
          thirdSlide.getBoundingClientRect().top + window.scrollY;
        this.threshold = slideDocTop + thirdSlide.offsetHeight / 2;
      }

      engage() {
        theme.variables.productPageSticky = true;
        this.formWrapper.classList.add(STICKY_CLASS);
        this.formWrapper.style.setProperty(
          "--sticky-top",
          `${this.stickyTop}px`
        );
        this.formWrapper.style.removeProperty("margin-top");
        this.isStuck = true;
        document.addEventListener("theme:scroll", this.scrollHandler);
      }

      teardown() {
        theme.variables.productPageSticky = false;
        this.formWrapper.classList.remove(STICKY_CLASS);
        this.formWrapper.style.removeProperty("--sticky-top");
        this.formWrapper.style.removeProperty("margin-top");
        this.isStuck = false;
        document.removeEventListener("theme:scroll", this.scrollHandler);
      }

      onScroll(event) {
        if (document.documentElement.hasAttribute("data-scroll-locked")) return;

        const scrollY = event.detail.position;

        if (scrollY < this.threshold && !this.isStuck) {
          // Scrolled back above threshold — re-engage sticky
          this.formWrapper.classList.add(STICKY_CLASS);
          this.formWrapper.style.setProperty(
            "--sticky-top",
            `${this.stickyTop}px`
          );
          this.formWrapper.style.removeProperty("margin-top");
          this.isStuck = true;
        } else if (scrollY >= this.threshold && this.isStuck) {
          // Crossed threshold — release sticky, freeze visual position with margin
          // Capture where the element sits visually while still stuck
          const stickyRect = this.formWrapper.getBoundingClientRect().top;

          // Remove sticky — triggers reflow on next layout read
          this.formWrapper.classList.remove(STICKY_CLASS);
          this.formWrapper.style.removeProperty("--sticky-top");

          // Measure where it naturally falls without sticky
          const flowRect = this.formWrapper.getBoundingClientRect().top;

          // Bridge the gap so the element stays in the same visual spot
          // Cap offset so form + margin never exceeds the images column height
          const offset = stickyRect - flowRect;
          if (offset > 0) {
            const mediaList = this.querySelector(SELECTORS.mediaList);
            const maxOffset = mediaList
              ? mediaList.offsetHeight - this.formWrapper.offsetHeight
              : Infinity;
            const cappedOffset = Math.min(offset, Math.max(0, maxOffset));
            if (cappedOffset > 0) {
              this.formWrapper.style.marginTop = `${cappedOffset}px`;
            }
          }

          this.isStuck = false;
        }
      }

      onResize() {
        this.formWrapper.style.removeProperty("margin-top");
        this.formWrapper.classList.remove(STICKY_CLASS);
        document.removeEventListener("theme:scroll", this.scrollHandler);
        requestAnimationFrame(() => this.init());
      }

      disconnectedCallback() {
        document.removeEventListener("theme:scroll", this.scrollHandler);
        document.removeEventListener("theme:resize", this.resizeHandler);
      }
    }
  );
})();
